import { UserButton, useUser } from '@clerk/clerk-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSessionStore } from '@/store/sessionStore';

type Props = {
  children: React.ReactNode;
  role: 'instructor' | 'student';
};

const navItems = [
  { to: '/', label: 'Home', roles: ['instructor', 'student'] },
  { to: '/lobby', label: 'Lobby', roles: ['student'] },
  { to: '/sessions', label: 'History', roles: ['instructor', 'student'] }
];

export const AppShell = ({ children, role }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { sessionId } = useSessionStore();

  const handleHistoryClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Check if user is in an active session
    if (sessionId) {
      const confirmed = window.confirm("This will end your current session. Continue?");
      if (!confirmed) {
        e.preventDefault();
        return false;
      }
    }
  };

  return (
    <div className="min-h-screen bg-background-main text-text-primary">
      <header className="sticky top-0 z-20 border-b border-background-border bg-background-main/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div>
            <p className="text-lg font-semibold text-brand">Digital Classroom Insights</p>
            <p className="text-xs text-text-dimmed capitalize">{role}</p>
          </div>
          <nav className="flex items-center gap-4">
            {navItems
              .filter((item) => item.roles.includes(role))
              .map((item) => {
                // Special handling for History link
                if (item.to === '/sessions') {
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={handleHistoryClick}
                      className={`text-sm ${location.pathname === item.to ? 'text-brand' : 'text-text-dimmed hover:text-text-primary'}`}
                    >
                      {item.label}
                    </Link>
                  );
                }
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`text-sm ${location.pathname === item.to ? 'text-brand' : 'text-text-dimmed hover:text-text-primary'}`}
                  >
                    {item.label}
                  </Link>
                );
              })}
          </nav>
          <div className="flex items-center gap-3">
            <div className="text-xs text-right">
              <p className="font-medium">{user?.fullName ?? 'User'}</p>
              <p className="text-text-dimmed">{user?.primaryEmailAddress?.emailAddress}</p>
            </div>
            <UserButton afterSignOutUrl="/signin" />
          </div>
        </div>
      </header>
      <main className="mx-auto min-h-[calc(100vh-64px)] max-w-7xl px-6 py-8">{children}</main>
    </div>
  );
};

